<script module>
  import AppLayout from '../../layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script>
  import { router } from '@inertiajs/svelte'
  import { Button } from '$lib/components/ui/button/index.js'
  import PageContainer from '$lib/components/page-container.svelte'
  import DownloadIcon from '@lucide/svelte/icons/download'
  import ChevronDownIcon from '@lucide/svelte/icons/chevron-down'

  let { properties_data, total_income_cents, total_deductions_cents, net_statutory_income_cents, year } = $props()

  // Track which property rows are expanded — initialised once from prop, intentional.
  // @svelte-ignore state_referenced_locally
  let expanded = $state(new Set(properties_data.map((_, i) => i)))

  function toggleProperty(i) {
    const next = new Set(expanded)
    if (next.has(i)) {
      next.delete(i)
    } else {
      next.add(i)
    }
    expanded = next
  }

  function formatRM(cents) {
    return (cents / 100).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  function formatRMNeg(cents) {
    return cents === 0 ? '—' : `(${formatRM(cents)})`
  }
</script>

<svelte:head>
  <title>Consolidated Tax Report — MyRentalTax</title>
</svelte:head>

<PageContainer>
  {#snippet breadcrumb()}
    <span class="text-[11px] font-medium text-muted-foreground">Consolidated Tax Report · All Properties</span>
  {/snippet}
    <!-- Header -->
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-1">
        <h1 class="text-xl sm:text-2xl font-semibold text-foreground m-0">Consolidated Tax Report</h1>
        <p class="text-sm text-muted-foreground m-0 leading-relaxed">Expand each property to review details.</p>
      </div>
      <Button variant="outline" size="sm" class="flex-shrink-0 gap-1.5" disabled>
        <DownloadIcon class="w-4 h-4" />
        <span class="hidden sm:inline">Download PDF</span>
        <span class="sm:hidden">Download</span>
      </Button>
    </div>

    <!-- Document card -->
    <div class="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden">

      <!-- Document header -->
      <div class="flex items-center justify-between px-5 sm:px-8 py-4 border-b border-zinc-100">
        <div class="flex items-center gap-2">
          <div class="w-7 h-7 bg-zinc-900 rounded-md flex items-center justify-center text-white flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/>
              <line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/>
              <line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/>
            </svg>
          </div>
          <span class="text-sm font-semibold text-zinc-900">Consolidated Report</span>
        </div>
        <span class="text-xs font-medium text-zinc-500">YA {year}</span>
      </div>

      <!-- Property accordion rows -->
      {#if properties_data.length === 0}
        <div class="px-5 sm:px-8 py-8 text-sm text-zinc-400 text-center">No properties found.</div>
      {:else}
        {#each properties_data as pdata, i (pdata.property.id)}
          {@const isOpen = expanded.has(i)}
          {@const isLast = i === properties_data.length - 1}

          <!-- Property row header -->
          <button
            class="w-full flex items-center justify-between px-5 sm:px-8 py-3 text-left hover:bg-zinc-50 transition-colors border-b border-zinc-100 {isLast && !isOpen ? 'border-b-0' : ''}"
            onclick={() => toggleProperty(i)}
          >
            <div class="flex items-center gap-2.5">
              <div class="w-5 h-5 rounded bg-zinc-100 flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="text-zinc-500">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </div>
              <span class="text-sm font-medium text-zinc-900">{pdata.property.name}</span>
            </div>
            <div class="flex items-center gap-3">
              <span class="text-sm font-semibold {pdata.statutory_income_cents < 0 ? 'text-red-600' : 'text-zinc-900'}">
                {pdata.statutory_income_cents === 0 ? 'RM 0.00' : `RM ${formatRM(pdata.statutory_income_cents)}`}
              </span>
              <ChevronDownIcon class="w-4 h-4 text-zinc-400 transition-transform {isOpen ? 'rotate-180' : ''}" />
            </div>
          </button>

          <!-- Expanded detail -->
          {#if isOpen}
            <div class="px-5 sm:px-8 py-4 bg-zinc-50 border-b border-zinc-100 {isLast ? 'border-b-0' : ''}">
              <div class="flex flex-col sm:flex-row gap-6">
                <!-- Income -->
                <div class="flex-1">
                  <p class="text-[10px] font-bold text-zinc-400 tracking-widest uppercase mb-2">Income</p>
                  {#if pdata.rooms.length === 0}
                    <div class="flex justify-between text-sm text-zinc-400 italic py-0.5">
                      <span>No rooms entered</span><span>—</span>
                    </div>
                  {:else}
                    {#each pdata.rooms as room}
                      <div class="flex justify-between text-sm py-0.5">
                        <span class="text-zinc-600 truncate mr-4">{room.name}</span>
                        <span class="text-zinc-900 flex-shrink-0">RM {formatRM(room.annual_income_cents)}</span>
                      </div>
                    {/each}
                  {/if}
                  <div class="flex justify-between text-sm font-semibold mt-1.5 pt-1.5 border-t border-zinc-200">
                    <span class="text-zinc-900">Gross Income</span>
                    <span class="text-zinc-900">RM {formatRM(pdata.gross_income_cents)}</span>
                  </div>
                </div>

                <!-- Deductions -->
                <div class="flex-1">
                  <p class="text-[10px] font-bold text-zinc-400 tracking-widest uppercase mb-2">Deductions</p>
                  <div class="flex flex-col gap-0.5">
                    <div class="flex justify-between text-sm py-0.5">
                      <span class="text-zinc-600">Loan Interest</span>
                      <span class="text-red-600">{formatRMNeg(pdata.deductions.loan_interest_cents)}</span>
                    </div>
                    <div class="flex justify-between text-sm py-0.5">
                      <span class="text-zinc-600">Cukai Taksiran</span>
                      <span class="text-red-600">{formatRMNeg(pdata.deductions.assessment_tax_cents)}</span>
                    </div>
                    <div class="flex justify-between text-sm py-0.5">
                      <span class="text-zinc-600">Cukai Tanah</span>
                      <span class="text-red-600">{formatRMNeg(pdata.deductions.quit_rent_tax_cents)}</span>
                    </div>
                    <div class="flex justify-between text-sm py-0.5">
                      <span class="text-zinc-600">Servis + Sinking Fund</span>
                      <span class="text-red-600">{formatRMNeg(pdata.deductions.service_sinking_cents)}</span>
                    </div>
                    <div class="flex justify-between text-sm py-0.5">
                      <span class="text-zinc-600">Utilities</span>
                      <span class="text-red-600">{formatRMNeg(pdata.deductions.utilities_cents)}</span>
                    </div>
                    {#if pdata.deductions.custom_expenses_cents > 0}
                      <div class="flex justify-between text-sm py-0.5">
                        <span class="text-zinc-600">Custom Expenses</span>
                        <span class="text-red-600">{formatRMNeg(pdata.deductions.custom_expenses_cents)}</span>
                      </div>
                    {/if}
                  </div>
                  <div class="flex justify-between text-sm font-semibold mt-1.5 pt-1.5 border-t border-zinc-200">
                    <span class="text-zinc-900">Total Deductions</span>
                    <span class="text-red-600">({formatRM(pdata.total_deductions_cents)})</span>
                  </div>
                </div>
              </div>
            </div>
          {/if}
        {/each}
      {/if}

      <!-- Combined totals -->
      <div class="px-5 sm:px-8 py-4 border-t border-zinc-100">
        <p class="text-[10px] font-bold text-zinc-400 tracking-widest uppercase mb-3">Combined Totals</p>
        <div class="flex justify-between text-sm py-0.5">
          <span class="text-zinc-600">Total Income</span>
          <span class="text-zinc-900">RM {formatRM(total_income_cents)}</span>
        </div>
        <div class="flex justify-between text-sm py-0.5">
          <span class="text-zinc-600">Total Deductions</span>
          <span class="text-red-600">({formatRM(total_deductions_cents)})</span>
        </div>
      </div>

      <!-- Statutory income banner -->
      <div class="mx-4 sm:mx-8 mb-5 mt-1 bg-zinc-900 rounded-xl px-5 py-5 flex flex-col gap-1.5">
        <p class="text-[10px] font-bold text-zinc-400 tracking-widest uppercase m-0">Net Statutory Income from Rents</p>
        <p class="text-xs text-zinc-500 m-0">Consolidated total across {properties_data.length} {properties_data.length === 1 ? 'property' : 'properties'}</p>
        <p class="text-3xl sm:text-4xl font-bold text-white m-0 mt-1">RM {formatRM(net_statutory_income_cents)}</p>
      </div>

      <!-- Badges -->
      <div class="px-5 sm:px-8 pb-5 flex items-center gap-3">
        <div class="flex items-center gap-1.5 text-xs text-zinc-500">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-green-500"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          LHDN compliant
        </div>
        <div class="flex items-center gap-1.5 text-xs text-zinc-500">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-green-500"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          Auto-calculated
        </div>
      </div>
    </div>

  {#snippet footer()}
    <Button variant="outline" onclick={() => router.visit('/properties')}>Back</Button>
    <div class="flex-1"></div>
    <Button onclick={() => router.visit('/properties/new')}>+ Add Another Property</Button>
  {/snippet}
</PageContainer>
